/**
 * ControlPanel API (`/broadcast-points`) クライアント。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 */
import type { AppConfig } from '../config';
import type { GtfsStop, GtfsStopListFile } from '../data/gtfsStops';
import { findPollyLanguage } from '../data/pollyVoices';
import type { RouteStop } from '../data/routeStops';

import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  BroadcastContent,
  BroadcastPointBulkEnabledResponse,
  BroadcastPointDeleteResult,
  BroadcastPointEnabledTarget,
  BroadcastPointCreateRequest,
  BroadcastPointListResponse,
  BroadcastPointSaveResult,
  BroadcastPointUpdateRequest,
} from './types';

export { ApiError, joinUrl };

/**
 * `POST /audio-preview` が文字数超過で返す 400 のメッセージ。
 *
 * 例: `text/ssml exceeds 3000 characters (got 3120)`。
 * `(got N)` は古いサーバ (件数なし) との互換のため任意として扱う。
 */
const PREVIEW_TOO_LONG_PATTERN = /^text\/ssml exceeds (\d+) characters(?: \(got (\d+)\))?$/;

/**
 * プレビューの文字数超過 (400) を、保存時の超過エラーと同じ見せ方の日本語にする。
 *
 * 保存時: `放送文面 (通常テキスト) は3000文字以内で入力してください（現在 3001 文字。超過すると放送できません）`
 * 試聴時: サーバの英語 (`text/ssml exceeds 3000 characters`) がそのまま出ていたため、
 * 同じ「上限 + 現在の文字数」の形に揃える。
 *
 * 試聴の文字数は **Polly へ渡す直前の SSML** (`<speak>` などのタグ、プレースホルダ展開後、
 * SSML 辞書の置換後) で数えるため、入力欄の文字数より多くなる。入力欄の文字数が上限以内でも
 * 超過し得るので、その旨を添えて「入力は上限以内なのに」という戸惑いを防ぐ。
 *
 * 対象外のメッセージは `null` を返す (呼び出し側は元のエラーをそのまま扱う)。
 */
export function describePreviewTooLongMessage(serverMessage: string): string | null {
  const match = serverMessage.match(PREVIEW_TOO_LONG_PATTERN);
  if (!match) return null;
  const current = match[2] !== undefined ? `現在 ${match[2]} 文字。` : '';
  return (
    `試聴する文面は${match[1]}文字以内にしてください` +
    `（${current}超過すると放送できません。タグや辞書の置換後の文字数も含みます）`
  );
}

/** サーバの `{text|ssml}_i18n.<language> is empty; ...` (外国語の文面が未入力) */
const PREVIEW_LOCALIZED_EMPTY_PATTERN = /^(text|ssml)_i18n\.(\S+) is empty;/;

/** 言語キーから画面に出す言語名を求める (未指定は日本語。マスタに無い値はキーのまま)。 */
function previewLanguageLabel(language: string | undefined): string {
  if (language === undefined || language === '') return '日本語';
  return findPollyLanguage(language)?.label ?? language;
}

/**
 * 「その言語の文面が空」のときの案内文を作る。
 *
 * 日本語欄が空のまま外国語だけ入力して試聴すると、既定の試聴言語 (日本語) で送られて
 * 英語のまま `text is required for input_mode=text` が出ていた。
 * 「どの言語の何が足りないか」と「次に何をすればよいか」を日本語で示す。
 */
function describeEmptyBody(language: string | undefined, kind: '通常テキスト' | 'SSML'): string {
  const label = previewLanguageLabel(language);
  const next =
    language === undefined || language === '' || language === 'ja'
      ? '日本語の文面を入力するか、試聴する言語を切り替えてください'
      : 'この言語の文面を入力してください';
  return `${label}の放送文面（${kind}）が入力されていません。${next}`;
}

/**
 * `POST /audio-preview` の 400 を、利用者向けの日本語メッセージにする。
 *
 * サーバ (`lambda/common/preview_render.py` ほか) の検証メッセージは英語のため、
 * そのまま出すと何を直せばよいか伝わらない。次のものを日本語へ置き換える:
 *
 * - 文字数超過 (`text/ssml exceeds N characters (got M)`)
 * - 文面が未入力 (`text is required for input_mode=text` / `ssml is required for input_mode=ssml`)
 * - 音声ファイル未アップロード (`audio_key is required for input_mode=audio`)
 * - 外国語の文面が未入力 (`text_i18n.<lang> is empty; ...` / `ssml_i18n.<lang> is empty; ...`)
 *
 * @param serverMessage サーバの 400 メッセージ
 * @param language 試聴を依頼した言語 (`previewAudio` の `language`。未指定は日本語)
 * @returns 日本語メッセージ。対象外のメッセージは `null` (呼び出し側は元のエラーをそのまま扱う)
 */
export function describePreviewErrorMessage(
  serverMessage: string,
  language?: string,
): string | null {
  const tooLong = describePreviewTooLongMessage(serverMessage);
  if (tooLong !== null) return tooLong;

  if (serverMessage === 'text is required for input_mode=text') {
    return describeEmptyBody(language, '通常テキスト');
  }
  if (serverMessage === 'ssml is required for input_mode=ssml') {
    return describeEmptyBody(language, 'SSML');
  }
  if (serverMessage === 'audio_key is required for input_mode=audio') {
    return '音声ファイル (MP3) がアップロードされていません。アップロードしてから試聴してください';
  }
  const localized = serverMessage.match(PREVIEW_LOCALIZED_EMPTY_PATTERN);
  if (localized) {
    // 言語はサーバが名指ししたものを優先する (依頼した言語と一致するはずだが、メッセージが正)。
    return (
      `${previewLanguageLabel(localized[2])}の放送文面（${localized[1] === 'ssml' ? 'SSML' : '通常テキスト'}）` +
      'が入力されていません。この言語の文面を入力してください' +
      '（空のままだと日本語が放送されてしまうため、試聴できません）'
    );
  }
  return null;
}

/**
 * `POST /audio-preview` の `sample_context` に載せる試聴用サンプル条件 (Issue #439)。
 *
 * フロント内部は camelCase で扱い、`previewAudio()` が snake_case へ変換して送る。
 * 省略したキーはサーバ側の既定サンプル (遅延 5 分など) が使われる。
 */
export interface PreviewSampleLineContextPayload {
  /** ATOS 線名コード (`{route_id}`)。サーバ側の突き合わせキーになる */
  routeId?: string;
  /** 線名 (`{route_name}` / `{route}`) */
  routeName?: string;
  /** この接続線の方面名 (`{direction_name}`) の一覧 */
  directionNames?: string[];
  /** この接続線だけ別の遅延で鳴らす場合の秒数 (未指定なら全体値) */
  delaySeconds?: number;
}

export interface PreviewSampleContextPayload {
  /** 遅延秒数 (`{delay_seconds}` / `{delay_minutes_*}` の展開元) */
  delaySeconds?: number;
  /** ATOS 線名コード (`{route_id}`) */
  routeId?: string;
  /** 一般的な線名 (`{route_name}` / `{route}`) */
  routeName?: string;
  /** 乗換駅名 (`{from_station}` / `{from_prefix}`)。part1 の接続線遅延で使う */
  fromStation?: string;
  /** 乗換駅名 (`{transfer_station}`)。part2 (乗換駅別) で使う */
  transferStation?: string;
  /** 方面名 (`{direction_name}`)。末尾の「方面行き」はサーバ側で自動付与される */
  directionName?: string;
  /** 発車見込み時刻 (24 時間表記の `HH:MM`)。発車見込み案内のみ (SBI-6) */
  departureTime?: string;
  /** 接続便の行先 (`{headsign}`)。発車見込み案内のみ (SBI-6) */
  headsign?: string;
  /**
   * 複数条件 (接続線 × 方面) — Issue #445。
   *
   * 接続線 / 方面は元データが配列なので、プレビュー条件も同じ多重度で送る。
   * 未指定なら従来どおり上のフラットキーのみが使われる (完全な後方互換)。
   */
  lines?: PreviewSampleLineContextPayload[];
}

/**
 * `POST /audio-preview` のレスポンス (Issue #439 で拡張)。
 *
 * `content_type` / `content_base64` は従来どおり必ず返る。
 * `rendered_ssml` 以降は **`content` を指定したときのみ** 付与される追加フィールドで、
 * 「なぜこう鳴ったか」をユーザーが自己解決できるようにするための診断情報。
 */
export interface AudioPreviewResponse {
  content_type: string;
  content_base64: string;
  /** サーバ側が実際に Polly へ渡した SSML (音声ファイル放送のときは空文字) */
  rendered_ssml?: string;
  /**
   * SSML 辞書を適用した後の SSML (SBI-03)。
   * 辞書で 1 件以上置換されたときだけ付与される (`rendered_ssml` は置換前のまま)。
   */
  applied_ssml?: string;
  /** `delay_patterns` のうち採用された行の index (該当なしは null) */
  matched_pattern_index?: number | null;
  /** 展開後に残った未対応プレースホルダ (誤ったトークン名の検知用) */
  unresolved_tokens?: string[];
  /** 音声ファイル放送 (`input_mode: 'audio'`) の場合に再生した S3 キー */
  audio_key?: string;
}

/**
 * `POST /translate` で 1 言語ぶんの翻訳が失敗したときの情報 (Issue #492 / SBI-11)。
 *
 * 1 言語の失敗で全体を落とすと成功した訳文も捨てられてしまうため、サーバは
 * 部分失敗を 200 + `failed[]` で返す。UI は `reason` をそのまま提示して
 * 運用者に手入力を促す。
 */
export interface TranslateFailure {
  /** 翻訳に失敗した language_key (`zh` など) */
  language: string;
  /**
   * 失敗理由。
   *
   * - `UnsupportedLanguage` … Amazon Translate 非対応言語。別言語で代替すると
   *   誤案内になるため翻訳されない (人手入力が必要)。Issue #545 で唯一の
   *   非対応言語だった広東語を放送対象から外したため現在は発生しないが、
   *   将来の言語追加に備えて表示経路は維持する。
   * - Amazon Translate の例外名 (`ThrottlingException` 等) … 時間をおいて
   *   再実行すれば成功し得る。
   */
  reason: string;
}

/**
 * `POST /translate` のレスポンス (Issue #492 / SBI-11)。
 *
 * ⚠️ **この内容はどこにも保存されていない**。呼び出し側は入力欄の「下書き」として
 * 反映し、人手修正のうえ明示的に保存 (`update()`) すること。
 */
export interface TranslateResponse {
  /** 翻訳に用いた原文 (リクエストの透過) */
  source_text: string;
  /** 実際に原文言語として扱った language_key (未指定時は `ja`) */
  source_language: string;
  /** language_key → 訳文。**成功した言語のみ**が入る (マスタ定義順) */
  translations: Record<string, string>;
  /** 翻訳できなかった言語と理由 (成功のみなら空配列) */
  failed: TranslateFailure[];
}

export interface BroadcastPointsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class BroadcastPointsClient {
  private readonly options: BroadcastPointsClientOptions;

  constructor(options: BroadcastPointsClientOptions) {
    this.options = options;
  }

  /** GET /broadcast-points (任意で routeDirection 絞り込み) */
  async list(routeDirection?: string): Promise<BroadcastPointListResponse> {
    const path = routeDirection
      ? `broadcast-points?routeDirection=${encodeURIComponent(routeDirection)}`
      : 'broadcast-points';
    return this.request<BroadcastPointListResponse>('GET', path);
  }

  /** POST /broadcast-points (新規作成) */
  async create(payload: BroadcastPointCreateRequest): Promise<BroadcastPointSaveResult> {
    return this.request<BroadcastPointSaveResult>('POST', 'broadcast-points', payload);
  }

  /** PUT /broadcast-points/{id} (更新) */
  async update(
    spotId: string,
    payload: BroadcastPointUpdateRequest,
  ): Promise<BroadcastPointSaveResult> {
    return this.request<BroadcastPointSaveResult>(
      'PUT',
      `broadcast-points/${encodeURIComponent(spotId)}`,
      payload,
    );
  }

  /**
   * POST /broadcast-points/bulk-enabled (有効 / 無効の部分更新 / Issue #679)
   *
   * 一覧の行トグル (targets 1 件) と一括切り替え (複数件) の両方で使う。
   * PUT (全置換) と違い `enabled` 以外の属性には触れない。
   */
  async setEnabled(
    targets: BroadcastPointEnabledTarget[],
    enabled: boolean,
  ): Promise<BroadcastPointBulkEnabledResponse> {
    return this.request<BroadcastPointBulkEnabledResponse>(
      'POST',
      'broadcast-points/bulk-enabled',
      { enabled, targets },
    );
  }

  /** DELETE /broadcast-points/{id} (履歴の登録結果を返す) */
  async remove(spotId: string, routeDirection: string): Promise<BroadcastPointDeleteResult> {
    const path = `broadcast-points/${encodeURIComponent(spotId)}?routeDirection=${encodeURIComponent(
      routeDirection,
    )}`;
    return this.request<BroadcastPointDeleteResult>('DELETE', path);
  }

  /**
   * POST /audio-uploads (MP3 音声ファイルをアップロードする)
   *
   * File を base64 化して送信し、保存先の `audio_key` を受け取る。
   * 受け取った `audio_key` は free_text コンテンツの `properties.audio_key` に設定する。
   */
  async uploadAudio(file: File): Promise<{ audio_key: string }> {
    const contentBase64 = await fileToBase64(file);
    return this.request<{ audio_key: string }>('POST', 'audio-uploads', {
      filename: file.name,
      content_type: 'audio/mpeg',
      content_base64: contentBase64,
    });
  }

  /**
   * GET /route-stops?route_id=...&direction_id=0
   *
   * 駅順マスタ (RouteStopList API) を ControlPanel API 経由 (Cognito 認証) で取得する。
   * 接続線遅延コンテンツの「遅延判定開始駅 / 終了駅」ドロップダウン用。
   */
  async getRouteStops(
    routeId: string,
    directionId = 0,
  ): Promise<{ route_id: string; direction_id?: number; stops: RouteStop[] }> {
    const path = `route-stops?route_id=${encodeURIComponent(
      routeId,
    )}&direction_id=${encodeURIComponent(String(directionId))}`;
    return this.request<{ route_id: string; direction_id?: number; stops: RouteStop[] }>(
      'GET',
      path,
    );
  }

  /**
   * GET /gtfs-stops[?date=YYYYMMDD]
   *
   * GTFS 静的 stops (GtfsStatic API `/gtfs/stops` のプロキシ) を ControlPanel API 経由
   * (Cognito 認証) で取得する。放送条件「停車番線」ドロップダウンの候補ソース。
   *
   * - レスポンスは `getRouteStops` の `stops` ではなく **`data`** キー
   *   (GtfsStatic のパススルー形 `{file, date, count, data}`)。本メソッドはその差異を
   *   吸収して `GtfsStop[]` に正規化して返す (欠損時は空配列)。
   * - `route_id` では絞り込めない (`stops.txt` に `route_id` カラムが無い) ため
   *   **全件取得**になる。呼び出し側でキャッシュし、駅選択後に `parent_station` で
   *   フィルタすること (`data/gtfsStops.ts` の `findPlatformStopIds` を使う)。
   * - `date` 省略時は GTFS 配置済みの最新日付が採用される (日付跨ぎ直後は翌日ダイヤの
   *   stops が返り得る)。運行日を固定したい場合のみ `YYYYMMDD` を指定する。
   */
  async getGtfsStops(date?: string): Promise<GtfsStop[]> {
    const path = date ? `gtfs-stops?date=${encodeURIComponent(date)}` : 'gtfs-stops';
    const res = await this.request<GtfsStopListFile>('GET', path);
    return res.data ?? [];
  }

  /**
   * POST /audio-preview (SSML / 任意テキストの音声プレビュー)
   *
   * 任意テキストまたは SSML を送り、Polly で合成した MP3 を base64 で受け取る。
   * 受け取った base64 は {@link base64ToAudioUrl} で再生用の object URL に変換できる。
   *
   * @param payload `text`（任意テキスト）または `ssml`（SSML 文字列）のいずれかを指定する。
   *   `text` を SSML として扱う場合は `isSsml: true` を指定する。
   *   `voiceId` に Polly VoiceId を指定すると実放送と同じ声色で試聴できる
   *   (未指定時は `language` の既定話者 / `language` も未指定ならサーバー側デフォルトの Takumi)。
   *   `language` に language_key (`ja` / `en` / `zh` / `ko` 等) を指定すると
   *   その言語で試聴できる (Issue #449)。
   */
  async previewAudio(payload: {
    text?: string;
    ssml?: string;
    isSsml?: boolean;
    voiceId?: string;
    /**
     * 試聴する言語 (language_key) — Issue #449。
     *
     * 指定すると Polly の `LanguageCode` / `Engine` / 既定話者をサーバ側の言語マスタ
     * から解決する。実放送 (UpdateContents) と同じマスタを使うため
     * 「プレビューで聴いた音声 = 本番の音声」が言語をまたいで成立する。
     *
     * 未指定 / 空文字は日本語 (従来と同一の挙動)。
     * `voiceId` と整合しない言語を指定するとサーバ側で 400 になるため、
     * 保存前に `isVoiceIdValidForLanguage()` で確認するとよい。
     */
    language?: string;
    /**
     * 放送コンテンツをまるごと渡してサーバ側でレンダリングさせる (Issue #439)。
     *
     * 指定すると `text` / `ssml` より優先され、サーバが UpdateContents と同じ
     * `ssml_builder` を使って実放送と同一の文面を組み立ててから合成する。
     * これにより「任意テキストの放送」以外もプレビューできる。
     */
    content?: BroadcastContent;
    /** `content` と併せて渡す試聴用のサンプル条件 (省略時はサーバ側既定値) */
    sampleContext?: PreviewSampleContextPayload;
    /**
     * SSML 辞書 (読み仮名辞典 / アクセント辞典) を適用して試聴するか (SBI-03)。
     *
     * 省略時はサーバ側既定の `true` (= 本番と同じ置換結果を試聴する)。
     * 利用者が書いた SSML をそのまま聴きたい画面 (SSML チューニング) や、
     * 保存前の辞書入力そのものを聴く画面 (SSML 辞書マスタ) は `false` を渡す。
     * `false` のときだけ `apply_dictionary: false` を送る
     * (`true` / 省略は既定と同じなので、既存クライアントのリクエストは変わらない)。
     */
    applyDictionary?: boolean;
  }): Promise<AudioPreviewResponse> {
    // voice_id はサーバー側でも「未指定 = Takumi」にフォールバックするが、
    // 空文字を送るとバリデーションで弾かれるため、空文字 / undefined はキーごと省く。
    const body: Record<string, unknown> = {
      text: payload.text,
      ssml: payload.ssml,
      is_ssml: payload.isSsml ?? false,
    };
    if (typeof payload.voiceId === 'string' && payload.voiceId !== '') {
      body.voice_id = payload.voiceId;
    }
    // language も voice_id と同じ規則: 空文字を送るとサーバ側 enum 検証で弾かれるため、
    // 空文字 / undefined はキーごと省く (= 未指定 = 日本語 = 従来の挙動)。
    if (typeof payload.language === 'string' && payload.language !== '') {
      body.language = payload.language;
    }
    if (payload.content !== undefined) {
      body.content = payload.content;
    }
    if (payload.applyDictionary === false) {
      body.apply_dictionary = false;
    }
    if (payload.sampleContext !== undefined) {
      // フロントは camelCase / API は snake_case なのでここで変換する。
      // undefined のキーは JSON.stringify で落ちるため、サーバ側では
      // 「未指定 = 既定サンプルを使う」として扱われる。
      body.sample_context = {
        delay_seconds: payload.sampleContext.delaySeconds,
        route_id: payload.sampleContext.routeId,
        route_name: payload.sampleContext.routeName,
        from_station: payload.sampleContext.fromStation,
        transfer_station: payload.sampleContext.transferStation,
        direction_name: payload.sampleContext.directionName,
        // 発車見込み案内 (SBI-6)。他のモードでは未指定 (サーバは無視する)。
        departure_time: payload.sampleContext.departureTime,
        headsign: payload.sampleContext.headsign,
        // 複数条件 (接続線 × 方面 / Issue #445)。未指定なら従来どおりフラットキーのみ。
        lines: payload.sampleContext.lines?.map((line) => ({
          route_id: line.routeId,
          route_name: line.routeName,
          direction_names: line.directionNames,
          delay_seconds: line.delaySeconds,
        })),
      };
    }
    try {
      return await this.request<AudioPreviewResponse>('POST', 'audio-preview', body);
    } catch (err) {
      // 既知の 400 (文字数超過 / 文面・音声の未入力) だけ、日本語メッセージに置き換える。
      // status / body は保持する (呼び出し側が `ApiError` として扱えるようにするため)。
      if (err instanceof ApiError && err.status === 400) {
        const message = describePreviewErrorMessage(err.message, payload.language);
        if (message !== null) throw new ApiError(err.status, message, err.body);
      }
      throw err;
    }
  }

  /**
   * POST /translate (日本語原文から各言語への機械翻訳) — Issue #492 / SBI-11
   *
   * 「日本語から翻訳」ボタンの実体。原文 (日本語) と翻訳先の language_key 配列を送り、
   * 言語別の訳文を受け取る。
   *
   * ## ⚠️ 翻訳結果は保存されない (誤案内防止)
   *
   * サーバは訳文を返すだけで DynamoDB / S3 への保存を **一切行わない**。
   * 機械翻訳の訳文をそのまま放送すると誤案内 (乗客の乗り間違い等) に直結するため、
   * 呼び出し側は結果を入力欄の **「下書き」として反映するだけ** に留め、
   * 保存は運用者の明示操作 (人手修正 → 保存ボタン → {@link update}) を必須とすること。
   *
   * ## 部分失敗
   *
   * 1 言語の失敗で全体が失敗扱いにならないよう、サーバは部分失敗を 200 + `failed[]`
   * で返す (`translations` には成功ぶんだけが入る)。呼び出し側は `failed` を
   * 走査してユーザーへエラー表示すること (Amazon Translate 非対応言語は
   * `reason: 'UnsupportedLanguage'`)。
   *
   * @param payload `sourceText` は必須。`targetLanguages` は最大 8 言語
   *   (サーバ側のレート制限。超過すると 400)。`sourceLanguage` は省略時 `ja`。
   */
  async translate(payload: {
    /** 翻訳したい原文 (通常は日本語)。プレーンテキストで渡すこと */
    sourceText: string;
    /** 翻訳先の language_key 配列 (`['en', 'zh', 'ko']` など / 最大 8 件) */
    targetLanguages: readonly string[];
    /** 原文の language_key。省略時はサーバ側の既定 (`ja`) */
    sourceLanguage?: string;
  }): Promise<TranslateResponse> {
    const body: Record<string, unknown> = {
      source_text: payload.sourceText,
      target_languages: [...payload.targetLanguages],
    };
    // 空文字を送るとサーバ側 enum 検証で弾かれるため、空文字 / undefined はキーごと省く
    // (= 未指定 = 日本語)。`voice_id` / `language` と同じ規則。
    if (typeof payload.sourceLanguage === 'string' && payload.sourceLanguage !== '') {
      body.source_language = payload.sourceLanguage;
    }
    return this.request<TranslateResponse>('POST', 'translate', body);
  }

  /**
   * GET /audio/{audio_key} (アップロード済み音源の presigned URL 取得) — Issue #439
   *
   * 管理画面で「アップロードした MP3 が正しいか」「音量・尺が適切か」を確認するための
   * エンドポイント。GetAudio Lambda が presigned URL を返すので、受け取った `url` を
   * `<audio src={url} controls>` にそのまま入れれば再生できる
   * (Polly を呼ばないので追加コストはゼロ、base64 変換も不要)。
   *
   * `audio_key` にはスラッシュ (`audio/xxx.mp3`) が含まれ得る。API Gateway は
   * `%2F` をデコードせずパスパラメータに渡すため、`encodeURIComponent` で
   * エスケープして送る (Lambda 側 `_decode_audio_key` が最大 3 回デコードする)。
   */
  async getAudioUrl(audioKey: string): Promise<{ audio_key: string; url: string }> {
    const path = `audio/${encodeURIComponent(audioKey)}`;
    return this.request<{ audio_key: string; url: string }>('GET', path);
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    // 204 No Content の場合は空 object を返す
    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }
}

/**
 * File を base64 文字列 (data URL プレフィックスなし) へ変換する。
 * FileReader.readAsDataURL の結果から `data:...;base64,` プレフィックスを除去して返す。
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * base64 でエンコードされた音声データを、再生可能な object URL (blob:) に変換する。
 *
 * `previewAudio` が返した `content_base64` を `<audio>` 要素などで再生する用途。
 * 使い終わった URL は呼び出し側で `URL.revokeObjectURL` を実行して解放すること。
 */
export function base64ToAudioUrl(
  contentBase64: string,
  contentType: string = 'audio/mpeg',
): string {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: contentType });
  return URL.createObjectURL(blob);
}
